'use client';

import { Box, useTheme } from '@mui/material';
import { avatarTone } from '@/lib/inbox/display';
import type { InboxMailLabel } from '@/lib/inbox/gmailLabels';

const LABEL_HEIGHT = 16;
/** Fits "Shopify Orders", the longest label in use. Longer names shorten, with the full name on hover. */
const LABEL_WIDTH = 96;

function MailLabel({ label }: { label: InboxMailLabel }) {
  const nameTone = avatarTone(label.name, useTheme().palette.mode === 'dark');
  const tone = label.color ?? nameTone;
  return (
    <Box
      component="span"
      title={label.name}
      sx={{
        width: LABEL_WIDTH,
        maxWidth: '100%',
        height: LABEL_HEIGHT,
        px: 0.625,
        borderRadius: '5px',
        textAlign: 'center',
        bgcolor: tone.bg,
        color: tone.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.01em',
        lineHeight: `${LABEL_HEIGHT}px`,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label.name}
    </Box>
  );
}

/** Every Gmail label on a thread, right-aligned on their own line under the preview so they never take room from the sender or the time. */
export default function MailLabels({ labels }: { labels: InboxMailLabel[] }) {
  if (!labels.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '3px 4px', mt: '3px' }}>
      {labels.map((label) => (
        <MailLabel key={label.name} label={label} />
      ))}
    </Box>
  );
}
